import { BudgetComparison, QuoteReview } from '@/components/JobCostPlanning';
/**
 * CP-4.2 — Booking screens.
 *
 *  - Lead inbox at /app/leads/me/bookings (data-testid='bookings-root')
 *  - Lead booking detail at /app/leads/me/bookings/{id}
 *  - Client booking wizard at /app/jobs/{job_id}/book/{lead_uuid}
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { CompanyRequests } from "@/pages/CompanyJobRequests";
import { toast } from "sonner";
import { ClientBudgetCard, clientBudget } from "@/lib/budget";

const STATE_TONE = {
  requested: "avcp-pill",
  accepted: "avcp-pill ok",
  declined: "avcp-pill warn",
  cancelled_by_client: "avcp-pill warn",
};

function StatePill({ state }) {
  return <span className={STATE_TONE[state] || "avcp-pill"} data-testid={TID.bookingState}>{state}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// ---------------------------------------------------------------------------
// LEAD INBOX
// ---------------------------------------------------------------------------

export function LeadBookingsInbox() {
  const { user } = useAuth();
  const isLead = user?.roles?.some(r => ["lead_contractor", "super_admin"].includes(r));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = isLead ? await api("/leads/me/bookings") : { data: [] };
        setItems(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Layout>
      <div data-testid={TID.bookingsRoot}>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Job Requests</h1>
        <p className="text-[var(--avcp-ink-soft)] mb-6">
          Review the project, compare the budget with your crew cost, and propose any changes to the hours. The client approves your quote before the job is confirmed.
        </p>
        <CompanyRequests />
        {isLead && (loading ? <div className="avcp-card p-6">Loading…</div> : (
          <ul className="space-y-3" data-testid={TID.bookingsList}>
            {items.length === 0 && <li className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]">No bookings yet.</li>}
            {items.map((b) => (
              <li key={b.id} className="avcp-card p-5" data-testid={TID.bookingRow}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Link to={`/app/leads/me/bookings/${b.id}`} className="font-semibold">{b.job_title}</Link>
                    <div className="text-xs text-[var(--avcp-ink-soft)] mt-1 flex items-center gap-2 flex-wrap">
                      <StatePill state={b.state} />
                      <span>{fmt(b.starts_at)} → {fmt(b.ends_at)}</span>
                      <span>· {b.region_label} · {b.service_city || "—"}</span>
                      <span>· {b.currency} {b.lead_rate_snapshot} lead / {b.helper_rate_standard_snapshot} helper</span>
                      <span>· {b.helper_count_requested} helper technicians</span>
                    </div>
                    {clientBudget(b) && <div className="mt-3"><ClientBudgetCard record={b} compact /></div>}
                    {b.client_message && <p className="text-sm mt-2">&ldquo;{b.client_message}&rdquo;</p>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ))}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// LEAD BOOKING DETAIL
// ---------------------------------------------------------------------------

export function LeadBookingDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    try {
      const r = await api(`/leads/me/bookings/${id}`);
      setData(r);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { refresh(); }, [id]);
  if (!data) return <Layout><div className="avcp-card p-6">Loading…</div></Layout>;
  const b = data.booking;
  const j = data.job;
  const decline = async () => {
    if (!window.confirm("Decline this booking?")) return;
    setBusy(true);
    try {
      await api(`/leads/me/bookings/${id}/decline`, { method: "POST", body: { reason: reason || null } });
      toast.success("Declined");
      await refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Layout>
      <div data-testid={TID.bookingDetailRoot}>
        <div className="flex items-center justify-between gap-2 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{j.title}</h1>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <StatePill state={b.state} />
              <span>{fmt(j.starts_at)} → {fmt(j.ends_at)}</span>
              <span>· {b.currency} {b.lead_rate_snapshot}/h lead · {b.helper_rate_standard_snapshot}/h helper · {b.helper_count_requested} helper technicians</span>
            </div>
          </div>
          {b.state === "requested" && (
            <div className="flex gap-2">
              
              <button className="avcp-btn ghost" disabled={busy} onClick={decline} data-testid={TID.bookingDecline}>Decline</button>
            </div>
          )}
          {(b.state === "accepted" || b.state === "crew_assigned") && (
            <Link to={`/app/leads/me/bookings/${id}/crew`} className="avcp-btn primary">
              Manage Crew
            </Link>
          )}
        </div>

        {clientBudget(b) && <div className="mb-4"><ClientBudgetCard record={b} /></div>}

        <QuoteReview bookingId={id} onChange={refresh} />

        {b.client_po_number && (
          <div className="avcp-card p-4 mb-4 text-sm">
            <span className="text-[var(--avcp-ink-soft)]">Purchase Order:</span>
            <span className="ml-2 font-mono">{b.client_po_number}</span>
            <span className="avcp-pill ml-2">
              {b.client_po_locked_at ? "Locked" : (b.client_po_source === "client_edited" ? "Edited" : "Auto-generated")}
            </span>
          </div>
        )}

        {b.state === "requested" && (
          <div className="avcp-card p-4 mb-4 grid md:grid-cols-2 gap-2">
            <input className="avcp-input" placeholder="Decline reason (optional)"
                   value={reason} onChange={(e) => setReason(e.target.value)}
                   data-testid={TID.bookingDeclineReason} />
          </div>
        )}

        <section className="avcp-card p-6 mb-4">
          <h2 className="font-semibold mb-2">Job</h2>
          {b.crew_requirements_json?.length > 0 && <div className="text-sm mb-3"><h3 className="font-semibold">Requested roles</h3><ul className="list-disc pl-5">{b.crew_requirements_json.map(r => <li key={r.title_code}>{r.quantity} × {r.title_code.replaceAll('_',' ')}</li>)}</ul></div>}
          <p className="text-sm whitespace-pre-line">{j.description || "—"}</p>
          <div className="grid md:grid-cols-2 gap-2 mt-3 text-sm">
            <div><span className="text-[var(--avcp-ink-soft)]">Region</span>: {j.region_id}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">City</span>: {j.service_city || "—"}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">Duration</span>: {j.job_duration_kind}</div>
            <div><span className="text-[var(--avcp-ink-soft)]">Helpers requested</span>: {j.helper_count_requested}</div>
          </div>
          {data.client_org && (
            <div className="mt-3 text-sm">
              <span className="text-[var(--avcp-ink-soft)]">Client</span>: {data.client_org.name}
              {data.client_org.website && <a className="ml-2 underline" target="_blank" rel="noreferrer" href={data.client_org.website}>{data.client_org.website}</a>}
            </div>
          )}
        </section>

        {data.address_revealed ? (
          <>
            <section className="avcp-card p-6 mb-4" data-testid={TID.bookingAddressBox}>
              <h2 className="font-semibold mb-2">Site address (revealed on acceptance)</h2>
              <p className="text-sm">{j.service_address}</p>
              <p className="text-sm">{j.service_city}{j.service_postal ? `, ${j.service_postal}` : ""}</p>
              <hr className="my-3 border-[var(--avcp-line)]" />
              <h3 className="font-medium mb-1">Meeting & site access</h3>
              <p className="text-sm">Meet: {j.meet_location || "—"} at {fmt(j.meet_at)}</p>
              {j.parking_instructions && <p className="text-sm">Parking: {j.parking_instructions}</p>}
              {j.site_access_notes && <p className="text-sm">Access: {j.site_access_notes}</p>}
              {j.notes_internal && (
                <>
                  <hr className="my-3 border-[var(--avcp-line)]" />
                  <h3 className="font-medium mb-1">Internal notes</h3>
                  <p className="text-sm whitespace-pre-line">{j.notes_internal}</p>
                </>
              )}
            </section>
            <section className="avcp-card p-6 mb-4" data-testid={TID.bookingContactsBox}>
              <h2 className="font-semibold mb-2">Contacts ({data.contacts.length})</h2>
              <ul className="space-y-2 text-sm">
                {data.contacts.map((c) => (
                  <li key={c.id} className="p-2 rounded bg-[var(--avcp-bg-2)]">
                    <div className="font-medium">{c.display_name} <span className="text-xs text-[var(--avcp-ink-soft)]">· {c.kind}{c.role_label ? ` · ${c.role_label}` : ""}</span></div>
                    {c.phone && <div>{c.phone}</div>}
                    {c.email && <div>{c.email}</div>}
                  </li>
                ))}
              </ul>
            </section>
            {data.packages.length > 0 && (
              <section className="avcp-card p-6">
                <h2 className="font-semibold mb-2">Packages</h2>
                <ul className="space-y-2">
                  {data.packages.map((p) => (
                    <li key={p.id} className="border border-[var(--avcp-line)] rounded p-3">
                      <div className="font-medium">{p.title} <span className="text-xs text-[var(--avcp-ink-soft)]">· {p.kind}</span></div>
                      <ul className="mt-2 text-sm space-y-1">
                        {p.versions.map((v) => (
                          <li key={v.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-[var(--avcp-bg-2)]">
                            <span className="font-mono text-xs">v{v.version}</span>
                            <span className="flex-1 text-xs text-[var(--avcp-ink-soft)] truncate">{fmt(v.created_at)} · {v.mime_type} · {(v.size_bytes/1024).toFixed(1)} KB</span>
                            <a className="avcp-btn ghost" href={v.download_url} target="_blank" rel="noreferrer">Download</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        ) : (
          <section className="avcp-card p-6 mb-4 border border-dashed border-[var(--avcp-line)]">
            <p className="text-sm text-[var(--avcp-ink-soft)]">
              Exact site address, contacts, meeting details, and package downloads will be revealed once you accept.
            </p>
          </section>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// CLIENT BOOKING WIZARD — initiated from contractor public profile
// ---------------------------------------------------------------------------

export function BookLeadWizard() {
  const route = useParams();
  const location = useLocation();
  const crewRequirements = useMemo(() => location.state?.crewRequirements || [], [location.state]);
  const [params] = useSearchParams();
  const leadUuid = route.lead_uuid || route.uuid;
  const companyId = route.company_id;
  const initialJobId = route.job_id || params.get('job') || '';
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(initialJobId);
  const [helperCount, setHelperCount] = useState(0);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recipient, setRecipient] = useState(null);
  const [cost, setCost] = useState(null);
  const [workHours, setWorkHours] = useState(8);
  const [targetBudget, setTargetBudget] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    Promise.all([api('/jobs?status=published'), api(companyId ? `/companies/${companyId}` : `/contractors/${leadUuid}`)])
      .then(([j,r]) => { if (active) { setJobs(j.data || []); setRecipient(companyId ? r : r.profile); } })
      .catch(e => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyId, leadUuid]);
  const job = jobs.find(j => String(j.id) === String(jobId));
  useEffect(() => { const searchCrew = crewRequirements.reduce((sum, r) => sum + Number(r.quantity || 0), 0); setHelperCount(searchCrew > 0 ? searchCrew - 1 : job?.helper_count_requested || 0); }, [job, crewRequirements]);
  useEffect(() => {
    if (!job) return;
    setWorkHours(location.state?.planning?.hours || Math.max(1, (new Date(job.ends_at)-new Date(job.starts_at))/3600000));
    setTargetBudget(location.state?.planning?.budget || clientBudget(job)?.total || '');
  }, [job]);
  useEffect(() => {
    if (!job || Number(helperCount)<0 || Number(helperCount)>20) return;
    let active=true; setCost(null);
    api(`/jobs/${job.id}/cost-preview`, {method:'POST',body:{
      ...(companyId?{contractor_org_id:Number(companyId)}:{lead_user_uuid:leadUuid}),
      helper_count_requested:Number(helperCount),crew_requirements:crewRequirements,
    }}).then(r=>{if(active)setCost(r);}).catch(e=>{if(active)setError(e.message);});
    return()=>{active=false;};
  }, [job, companyId, leadUuid, helperCount, crewRequirements]);
  const name = companyId ? recipient?.display_name || recipient?.legal_name : recipient?.company_name;
  const submit = async () => {
    if (!job || !recipient) return;
    setBusy(true); setError('');
    try {
      await api(`/jobs/${job.id}/booking-request`, { method: 'POST', body: {
        ...(companyId ? { contractor_org_id: Number(companyId) } : { lead_user_uuid: leadUuid }),
        helper_count_requested: Number(helperCount), client_message: message || null,
        planned_work_hours: Number(workHours), client_budget_total: Number(targetBudget),
        ...(crewRequirements.length ? { crew_requirements: crewRequirements } : {}),
      }});
      toast.success('Job request sent directly to your contractor');
      navigate(`/app/jobs/${job.id}`);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  return <Layout><div data-testid={TID.bookWizardRoot}>
    <Link className="underline text-sm" to={`/app/find-contractors${jobId ? `?job=${jobId}` : ''}`}>Back to contractor search</Link>
    <h1 className="text-3xl font-bold mt-3">Send job request</h1>
    <p className="text-[var(--avcp-ink-soft)] mt-2 mb-6">Send your project directly to this contractor. Compare your budget with the crew cost, then send your request. The contractor reviews the hours and sends a quote for your approval.</p>
    {error && <p role="alert" className="avcp-card p-4 text-rose-700 mb-4">{error}</p>}
    {loading ? <p>Loading project and contractor…</p> : <>
      <div className="avcp-card p-5 mb-4"><h2 className="font-semibold">Selected {companyId ? 'company' : 'contractor'}: {name || 'Contractor'}</h2>
        {companyId ? <p className="text-sm mt-2">The company receives the request and assigns its lead when accepting.</p> : <p className="text-sm mt-2">The lead reviews the project and arranges the additional crew.</p>}
        {recipient?.operational_status === 'vacation' && <p className="text-amber-800 text-sm mt-2">On vacation — they may decline this request.</p>}
      </div>
      <section className="avcp-card p-5 space-y-4">
        <label className="grid gap-1 text-sm">Published project
          <select className="avcp-input" value={jobId} onChange={e => setJobId(e.target.value)} data-testid={TID.bookWizardJob}>
            <option value="">Choose a project…</option>{jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        </label>
        {!jobs.length && <p>Create and publish a project in <Link className="underline" to="/app/jobs">Projects</Link> first.</p>}
        {initialJobId && !jobs.some(j => String(j.id) === String(initialJobId)) && <p className="text-sm">The linked project is no longer available for a new request. Choose another published project.</p>}
        {job && <div data-testid={TID.bookWizardSummary}><p className="text-sm mb-3">{fmt(job.starts_at)} – {fmt(job.ends_at)} · {job.currency}</p></div>}
        {crewRequirements.length > 0 && <div className="text-sm"><h2 className="font-semibold">Requested roles from your search</h2><ul className="list-disc pl-5 mt-2">{crewRequirements.map(r => <li key={r.title_code}>{r.quantity} × {r.title_code.replaceAll('_',' ')}</li>)}</ul><p className="mt-2">These requirements go to the contractor with your project. Confirm the total additional crew count below.</p></div>}
        <label className="grid gap-1 text-sm">Additional crew members requested
          <input type="number" min="0" max="20" className="avcp-input" value={helperCount} onChange={e => setHelperCount(e.target.value)} data-testid={TID.bookWizardHelperCount} />
        </label>
        {job && <div className="space-y-4 border-t pt-4">
          <h2 className="text-xl font-semibold">Check your budget and working hours</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="grid gap-1 text-sm">Working hours per crew member<input className="avcp-input" type="number" min="0.01" max="10000" step="0.25" value={workHours} onChange={e=>setWorkHours(e.target.value)} /></label>
            <label className="grid gap-1 text-sm">Total project budget ({job.currency})<input className="avcp-input" type="number" min="0.01" max="100000000" step="0.01" value={targetBudget} onChange={e=>setTargetBudget(e.target.value)} /></label>
          </div>
          <p className="text-xs">Enter working hours per person, excluding nights and breaks. This does not change the scheduled project dates. Your budget is saved with this request.</p>
          <BudgetComparison workTerms={cost?.work_terms} hours={workHours} budget={targetBudget} rateLow={cost?.rate_low} rateHigh={cost?.rate_high} currency={job.currency} crewSize={Number(helperCount)+1}/>
          {cost?.source&&<p className="text-xs">{cost.source}. The contractor confirms the hours and price in the quote.</p>}
        </div>}
        <label className="grid gap-1 text-sm">Message to contractor (optional)
          <textarea className="avcp-input" rows="3" maxLength="2000" value={message} onChange={e => setMessage(e.target.value)} data-testid={TID.bookWizardMessage} />
        </label>
        <p className="text-sm">The contractor receives the project scope, dates, budget and crew needs. Sending a request does not mean it has been accepted.</p>
        <button className="avcp-btn primary" disabled={busy || !job || !recipient || Number(helperCount) < 0 || Number(helperCount) > 20 || Number(workHours)<=0 || Number(workHours)>10000 || Number(targetBudget)<=0 || Number(targetBudget)>100000000} onClick={submit} data-testid={TID.bookWizardSubmit}>{busy ? 'Sending…' : 'Send job request'}</button>
      </section>
    </>}
  </div></Layout>;
}
